/* eslint-disable @next/next/no-img-element */
"use client";

import { useState, useEffect } from "react";

/**
 * SoonPage (coming-soon / partial-closure landing page)
 *
 * - Shows a full-screen gradient background with the Rawiah logo.
 * - Animates the word "Soon" in multiple languages, cycling every 2 seconds
 *   using the `translations` array and a simple interval + CSS keyframes.
 * - On mount, fetches the visitor's public IP (via https://api.ipify.org)
 *   and then looks up their country using the IPify Geo API with
 *   `process.env.NEXT_PUBLIC_IPIFY_API`.
 * - Renders an email input where users can register their interest:
 *   - Before submitting, it checks `/api/interests/check-email` to see if the
 *     email already exists; if it does, it shows a yellow warning alert.
 *   - If the email is new, it posts to `/api/register-interest` with the
 *     `email` and detected `country`, then shows a "Thank You" message.
 * - The "Continue to website" button navigates to `/home`.
 */
export default function SoonPage() {
  const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const translations = [
    { language: "English", word: "Soon" },
    { language: "Chinese", word: "很快" },
    { language: "Spanish", word: "Pronto" },
    { language: "French", word: "Bientôt" },
    { language: "German", word: "Bald" },
    { language: "Arabic", word: "قريبًا" },
    { language: "Hindi", word: "जल्द ही" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentLanguageIndex(
          (prevIndex) => (prevIndex + 1) % translations.length,
        );
        setIsVisible(true);
      }, 500);
    }, 2000);

    return () => clearInterval(interval);
  }, [translations.length]);

  const handleSubmit = async () => {
    if (!email) return;
    setIsSubmitting(true);
    setShowAlert(false);

    try {
      const checkResponse = await fetch(`/api/interests/check-email?email=${encodeURIComponent(email)}`);
      const checkData = await checkResponse.json();
      if (checkData.exists) {
        setShowAlert(true);
        setIsSubmitting(false);
        return;
      }

      await fetch("/api/interests/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Error registering interest:", error);
    }

    setIsSubmitting(false);
  };

  return (
    <div className="flex h-screen flex-col items-center justify-center bg-linear-to-r from-[#27335D] to-[#4887f5] p-8 text-white">
      <img src="/images/logo.svg" alt="Rawiah Logo" className="mb-10" />
      <div className="mb-10 flex h-[100px] items-center overflow-hidden text-center">
        <h1
          className={`text-5xl font-bold transition-all duration-500 ease-in-out md:text-7xl ${
            isVisible ? "animate-slide-up" : "animate-slide-out"
          }`}
          key={translations[currentLanguageIndex].word}
        >
          {translations[currentLanguageIndex].word}
        </h1>
      </div>
      {showAlert && (
        <div
          className="mb-4 flex items-center rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800"
          role="alert"
        >
          <svg
            className="me-3 inline h-4 w-4 shrink-0"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
          </svg>
          <span className="sr-only">Info</span>
          <div>This email is already registered.</div>
        </div>
      )}
      {!submitted ? (
        <div className="flex rounded-full border-2 border-black shadow-xl">
          <input
            data-testid="comingSoonEmailInput"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            autoComplete="email"
            className="h-20 rounded-l-full border-none bg-white px-4 py-3 text-gray-800 outline-none focus:ring-2 focus:ring-blue-300 lg:w-96"
          />
          <button
            data-testid="comingSoonSubmitButton"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`transform rounded-r-full px-6 py-3 text-lg font-extrabold text-white transition ${
              isSubmitting
                ? "cursor-not-allowed bg-gray-400"
                : "cursor-pointer bg-[#f7c65a] hover:scale-105 hover:bg-blue-100 hover:text-[#f7c65a]"
            }`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      ) : (
        <div className="text-center">
          <h2 className="mb-4 text-4xl font-bold">Thank You!</h2>
          <p>We have received your interest. Stay tuned!</p>
        </div>
      )}
      <p data-testid="soonPageMessage" className="my-6 max-w-xl px-4 text-center text-4xl font-semibold">
        Stay tuned for our launch and receive special offers straight to your
        inbox📥!
      </p>
      <style jsx>{`
        @keyframes slide-up {
          0% {
            transform: translateY(100%);
            opacity: 0;
          }
          50% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slide-out {
          0% {
            transform: translateY(0);
            opacity: 1;
          }
          100% {
            transform: translateY(-100%);
            opacity: 0;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.5s ease-in-out forwards;
        }

        .animate-slide-out {
          animation: slide-out 0.5s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
}
